import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { LOGO_URL } from "@/lib/translations";
import { LayoutDashboard, FileText, Building2, BellRing, Users, LogOut, ShieldCheck, Menu, X, Archive, Mail } from "lucide-react";

export default function Layout({ children }) {
  const { user, logout, isAdmin, isSuperadmin } = useAuth();
  const { t, lang, changeLang } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  const nav = [
    { to: "/", icon: LayoutDashboard, label: t("dashboard"), end: true, id: "dashboard" },
    { to: "/documents", icon: FileText, label: t("documents"), id: "documents" },
    { to: "/archived", icon: Archive, label: t("archivedDocuments"), id: "archived" },
    { to: "/correspondence", icon: Mail, label: t("correspondence"), id: "correspondence" },
    { to: "/companies", icon: Building2, label: t("companies"), id: "companies" },
    { to: "/alerts", icon: BellRing, label: t("alerts"), id: "alerts" },
    ...(isAdmin ? [{ to: "/users", icon: Users, label: t("users"), id: "users" }] : []),
  ];

  const getRoleLabel = () => {
    if (isSuperadmin) return t("superadmin");
    if (isAdmin) return t("admin");
    return t("staff");
  };
  const roleLabel = getRoleLabel();

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const SidebarInner = () => (
    <div className="flex h-full flex-col border-r bg-card">
      <div className="border-b px-5 py-5">
        <img src={LOGO_URL} alt="Horizonte Documenta" className="h-9 w-auto object-contain" data-testid="sidebar-logo" />
      </div>

      <nav className="flex-1 space-y-1 px-3 py-4">
        {nav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={`nav-${item.id}`}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              cn(
                "group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                isActive ? "bg-horizonte-orange text-white" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              )
            }
          >
            <item.icon className="h-4 w-4" />
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="space-y-3 border-t p-4">
        <div className="flex gap-1 rounded-lg bg-secondary p-1" data-testid="language-toggle">
          {["en", "pt"].map((l) => (
            <button
              key={l}
              data-testid={`language-toggle-${l}`}
              onClick={() => changeLang(l)}
              className={cn(
                "flex-1 rounded-md py-1.5 text-xs font-semibold uppercase transition-colors",
                lang === l ? "bg-horizonte-orange text-white" : "text-muted-foreground hover:text-foreground"
              )}
            >
              {l === "en" ? "EN" : "PT"}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3 rounded-lg bg-secondary px-3 py-2.5">
          {user?.picture ? (
            <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" referrerPolicy="no-referrer" />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-horizonte-orange text-sm font-bold text-white">
              {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium" data-testid="user-name">{user?.name || user?.email}</div>
            <div className="flex items-center gap-1 text-[11px] font-medium text-horizonte-orange">
              <ShieldCheck className="h-3 w-3" /> {roleLabel}
            </div>
          </div>
        </div>

        <Button variant="ghost" onClick={handleLogout} data-testid="logout-button" className="w-full justify-start text-muted-foreground hover:text-foreground">
          <LogOut className="mr-2 h-4 w-4" /> {t("logout")}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="hidden w-64 shrink-0 lg:block">
        <div className="fixed h-screen w-64"><SidebarInner /></div>
      </aside>

      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-64 animate-fade-up"><SidebarInner /></div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col lg:pl-0">
        <header className="flex items-center gap-3 border-b bg-card px-4 py-3 lg:hidden">
          <button data-testid="mobile-menu-button" onClick={() => setOpen(true)} className="rounded-md p-2 hover:bg-secondary">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <img src={LOGO_URL} alt="Horizonte Documenta" className="h-7 w-auto object-contain" />
        </header>
        <main className="flex-1 px-5 py-6 sm:px-8 sm:py-8">{children}</main>
      </div>
    </div>
  );
}
